import { useCallback, useEffect, useState } from 'react'
import { appConfig } from '@config'

import { MAP_STATION_CODE } from '@constants'

// Типы для безопасности
interface MapStyleSource {
  type: 'vector' | 'raster' | 'geojson'
  url: string

  [key: string]: any
}

interface MapStyle {
  version: number
  sources: Record<string, MapStyleSource>
  layers: any[]
  glyphs?: string
  sprite?: string

  [key: string]: any
}

// Константы выносим отдельно
const STYLE_PATH = '/map-styles/bright-offline.json'
const DEFAULT_STATION_CODE = MAP_STATION_CODE.VKO
const BASE_PMTILES_PATH = 'pmtiles:///map-data/'

// Базовый стиль как fallback
const FALLBACK_STYLE: MapStyle = {
  version: 8,
  sources: {},
  layers: [],
  glyphs: '/map-styles/fonts/{fontstack}/{range}.pbf',
  sprite: '/map-styles/sprites/ofm',
}

/**
 * Утилиты для работы со стилем
 */
const createPmtilesUrl = (fileName: string): string => {
  const hasExtension = fileName.endsWith('.pmtiles')

  return `${BASE_PMTILES_PATH}${hasExtension ? fileName : `${fileName}.pmtiles`}`
}

const getStationCode = (pmTilesFile?: string): string => {
  if (pmTilesFile) return pmTilesFile

  return appConfig.mapStationCode || DEFAULT_STATION_CODE
}

const validateAndCloneStyle = (data: any): MapStyle => {
  const styleCopy = JSON.parse(JSON.stringify(data)) as MapStyle

  // Гарантируем обязательные поля
  if (!styleCopy.sources || typeof styleCopy.sources !== 'object') {
    styleCopy.sources = {}
  }

  if (!Array.isArray(styleCopy.layers)) {
    styleCopy.layers = []
  }

  if (!styleCopy.version || typeof styleCopy.version !== 'number') {
    styleCopy.version = 8
  }

  return styleCopy
}

const updateStyleSource = (style: MapStyle, pmtilesUrl: string): MapStyle => {
  if (style.sources?.openmaptiles) {
    style.sources.openmaptiles.url = pmtilesUrl
  } else {
    style.sources.openmaptiles = {
      type: 'vector',
      url: pmtilesUrl,
    }
  }

  return style
}

const createFallbackStyle = (fileName: string): MapStyle => ({
  ...FALLBACK_STYLE,
  sources: {
    openmaptiles: {
      type: 'vector',
      url: createPmtilesUrl(fileName),
    },
  },
})

/**
 * Хук для загрузки и управления стилями карты
 */
export const useMapStyle = (pmTilesFile?: string) => {
  const [state, setState] = useState<{
    style: MapStyle
    isLoading: boolean
    error: Error | null
  }>({
    style: FALLBACK_STYLE,
    isLoading: true,
    error: null,
  })

  const loadStyle = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }))

      const response = await fetch(STYLE_PATH)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to load map style`)
      }

      const data = await response.json()

      const stationCode = getStationCode(pmTilesFile)
      const pmtilesUrl = createPmtilesUrl(stationCode)

      // Обработка и валидация стиля
      const validatedStyle = validateAndCloneStyle(data)
      const updatedStyle = updateStyleSource(validatedStyle, pmtilesUrl)

      setState({
        style: updatedStyle,
        isLoading: false,
        error: null,
      })
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error loading map style')
      console.error('Map style loading error:', error)

      // Создаем fallback стиль
      const stationCode = getStationCode(pmTilesFile)
      const fallbackStyle = createFallbackStyle(stationCode)

      setState({
        style: fallbackStyle,
        isLoading: false,
        error,
      })
    }
  }, [pmTilesFile])

  useEffect(() => {
    loadStyle()
  }, [loadStyle])

  // Метод для ручного обновления стиля
  const refresh = useCallback(() => {
    loadStyle()
  }, [loadStyle])

  return {
    style: state.style,
    isLoading: state.isLoading,
    error: state.error,
    refresh,
  }
}

// Вспомогательный хук с дефолтными настройками
export const useDefaultMapStyle = () => {
  return useMapStyle()
}
